import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';

/** What Firebase stores at config/documentCategories/{key} */
export interface DocumentCategoryData {
    label: string;
}

/** Enriched type with the Firebase key included, used by consumers */
export interface DocumentCategory {
    key: string;
    label: string;
}

interface DocumentCategoriesContextValue {
    categories: DocumentCategory[];
    loading: boolean;
    refresh: () => Promise<void>;
}

const DocumentCategoriesContext = createContext<DocumentCategoriesContextValue | undefined>(undefined);

export const useDocumentCategories = () => {
    const context = useContext(DocumentCategoriesContext);
    if (!context) {
        throw new Error('useDocumentCategories must be used within a DocumentCategoriesProvider');
    }
    return context;
};

export const DocumentCategoriesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [categories, setCategories] = useState<DocumentCategory[]>([]);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

    const fetchCategories = useCallback(async () => {
        try {
            const db = getDatabase();
            const snapshot = await get(ref(db, 'config/documentCategories'));

            if (snapshot.exists()) {
                const data = snapshot.val() as Record<string, DocumentCategoryData>;
                const parsed: DocumentCategory[] = Object.entries(data)
                    .map(([key, val]) => ({ key, label: val.label }))
                    .sort((a, b) => a.label.localeCompare(b.label, 'sv'));
                setCategories(parsed);
            } else {
                setCategories([]);
            }
        } catch (err) {
            console.error('Error fetching document categories:', err);
            setCategories([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setIsAuthenticated(!!user);
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (isAuthenticated === null) return;
        if (!isAuthenticated) {
            setLoading(false);
            return;
        }
        setLoading(true);
        fetchCategories();
    }, [isAuthenticated, fetchCategories]);

    const refresh = useCallback(async () => {
        await fetchCategories();
    }, [fetchCategories]);

    const value = useMemo(() => ({
        categories,
        loading,
        refresh,
    }), [categories, loading, refresh]);

    return (
        <DocumentCategoriesContext.Provider value={value}>
            {children}
        </DocumentCategoriesContext.Provider>
    );
};
